import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../utils/trpc.js';

/**
 * The write-access advisory, shared by everywhere a screen names an environment before
 * writing to it: the Environments card and the push modal's checklist.
 *
 * `columns` (from `matrix.roots` / `matrix.root`) carries no `boGroupPrefix`, only
 * `environments.list` does, so this asks for both itself rather than threading them through
 * every caller. Advisory only — nothing here disables anything; it only tells someone in
 * advance that the environment's backoffice will refuse every operation without its admin
 * group, since applying forwards the caller's own token and that backoffice decides.
 */
export const useWriteAccess = () => {
    const trpc = useTRPC();
    // `staleTime: Infinity`: who is signed in and what they hold never changes as a side effect
    // of a write this client makes, so a mutation's invalidation skips it too
    // (`utils/invalidation.ts`).
    const me = useQuery({ ...trpc.users.me.queryOptions(), staleTime: Infinity });
    const environments = useQuery(trpc.environments.list.queryOptions());

    /** `<boGroupPrefix>admin` for a known environment, or `null` before `environments.list` answers. */
    const adminGroup = (environmentName: string): string | null => {
        const env = environments.data?.find((candidate) => candidate.name === environmentName);
        return env ? `${env.boGroupPrefix}admin` : null;
    };

    /** `true` once `users.me` and `environments.list` have both answered and the group is held. */
    const canWrite = (environmentName: string): boolean => {
        const group = adminGroup(environmentName);
        if (!group || !me.data) return false;
        return me.data.allGroups.includes(group);
    };

    /**
     * The one line every write confirmation prepends: `null` before both queries have
     * answered (never a false "you lack it" flash) and once the group is held.
     */
    const advisory = (environmentName: string): string | null => {
        if (!me.data || !environments.data) return null;
        const group = adminGroup(environmentName);
        if (!group || canWrite(environmentName)) return null;
        return `You do not hold ${group}; every operation will be refused.`;
    };

    // Exposed so a caller that also needs the environment list or the session (the push modal's
    // risk rule and its `canApply` gate) reads them off this hook instead of asking twice.
    return { canWrite, adminGroup, advisory, me, environments };
};
